<script setup lang="ts">
import type { CampaignStatistics } from '@/types/types'

const props = defineProps<{
  statistics: CampaignStatistics
}>()

const { t } = useI18n()
const { formatNumber, formatPercentage, formatCurrency } = useNumber()

const emailOpenedPercentage = computed(() => {
  if (!props.statistics.emailDelivered || !props.statistics.emailOpenedUnique) return 0
  return (props.statistics.emailOpenedUnique / props.statistics.emailDelivered) * 100
})

const emailClickedPercentage = computed(() => {
  if (!props.statistics.emailDelivered || !props.statistics.emailClickedUnique) return 0
  return (props.statistics.emailClickedUnique / props.statistics.emailDelivered) * 100
})
</script>

<template>
  <div class="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-5 mb-6">
    <AppStatsCard>
      <template #title>
        <div class="flex items-center gap-2">
          <span class="truncate text-sm font-medium uppercase tracking-wider text-foreground-secondary">
            {{ t('pages.campaign.stats.messagesCount') }}
          </span>
        </div>
      </template>
      <template #content>
        <div class="flex flex-col flex-wrap items-baseline justify-between gap-1 md:flex-row">
          <span class="text-3xl font-extrabold text-primary">
            {{ formatNumber(statistics.messagesCount) }}
          </span>
        </div>
      </template>
    </AppStatsCard>
    <AppStatsCard>
      <template #title>
        <div class="flex items-center gap-2">
          <span class="truncate text-sm font-medium uppercase tracking-wider text-foreground-secondary">
            {{ t('pages.campaign.stats.opensRate') }}
          </span>
          <AppTooltip width="w-[360px]" tooltip-position="bottom">
            <BaseIcon name="IconoirInfoCircle" class="h-4 w-4 cursor-pointer text-foreground-secondary" />
            <template #tooltip-content>
              <div class="flex flex-col gap-2 text-left">
                <p>{{ t('pages.campaign.stats.opensTooltip') }}</p>
              </div>
            </template>
          </AppTooltip>
        </div>
      </template>
      <template #content>
        <div class="flex flex-col flex-wrap items-baseline justify-start gap-3 md:flex-row">
          <span class="text-3xl font-extrabold text-primary">
            {{ statistics.emailOpenedUnique ? formatNumber(statistics.emailOpenedUnique) : '-' }}
          </span>
          <span v-if="emailOpenedPercentage > 0" class="text-muted-foreground">
            {{ formatPercentage(emailOpenedPercentage, emailOpenedPercentage < 1 ? 2 : 1) }}
          </span>
        </div>
      </template>
    </AppStatsCard>
    <AppStatsCard>
      <template #title>
        <div class="flex items-center gap-2">
          <span class="truncate text-sm font-medium uppercase tracking-wider text-foreground-secondary">
            {{ t('pages.campaign.stats.clicksRate') }}
          </span>
          <AppTooltip width="w-[360px]" tooltip-position="bottom">
            <BaseIcon name="IconoirInfoCircle" class="h-4 w-4 cursor-pointer text-foreground-secondary" />
            <template #tooltip-content>
              <div class="flex flex-col gap-2 text-left">
                <p class="font-semibold">{{ t('pages.campaign.stats.clickTooltipTitle') }}</p>
                <p class="font-semibold">{{ t('pages.campaign.stats.tooltipCountingRuleTitle') }}</p>
                <ul class="list-disc pl-5 space-y-1">
                  <li>{{ t('pages.campaign.stats.clickTooltipCountingRuleItem1') }}</li>
                  <li>{{ t('pages.campaign.stats.clickTooltipCountingRuleItem2') }}</li>
                </ul>
                <p class="font-semibold">{{ t('pages.campaign.stats.tooltipExampleTitle') }}</p>
                <ul class="list-disc pl-5 space-y-1">
                  <li>{{ t('pages.campaign.stats.clickTooltipExampleItem1') }}</li>
                </ul>
              </div>
            </template>
          </AppTooltip>
        </div>
      </template>
      <template #content>
        <div class="flex flex-col flex-wrap items-baseline justify-start gap-3 md:flex-row">
          <span class="text-3xl font-extrabold text-primary">
            {{ statistics.emailClickedUnique ? formatNumber(statistics.emailClickedUnique) : '-' }}
          </span>
          <span v-if="emailClickedPercentage > 0" class="text-muted-foreground">
            {{ formatPercentage(emailClickedPercentage, emailClickedPercentage < 1 ? 2 : 1) }}
          </span>
        </div>
      </template>
    </AppStatsCard>
    <AppStatsCard>
      <template #title>
        <div class="flex items-center gap-2">
          <span class="truncate text-sm font-medium uppercase tracking-wider text-foreground-secondary">
            {{ t('pages.campaign.stats.visitsRate') }}
          </span>
          <AppTooltip width="w-[360px]" tooltip-position="bottom">
            <BaseIcon name="IconoirInfoCircle" class="h-4 w-4 cursor-pointer text-foreground-secondary" />
            <template #tooltip-content>
              <div class="flex flex-col gap-2 text-left">
                <p class="font-semibold">{{ t('pages.campaign.stats.conversionTooltipTitle') }}</p>
                <p>{{ t('pages.campaign.stats.conversionTooltipReturnCountedTitle') }}</p>
                <ul class="list-disc pl-5 space-y-1">
                  <li>{{ t('pages.campaign.stats.conversionTooltipReturnCountedItem1') }}</li>
                  <li>{{ t('pages.campaign.stats.conversionTooltipReturnCountedItem2') }}</li>
                </ul>
                <p class="font-semibold">{{ t('pages.campaign.stats.tooltipCountingRuleTitle') }}</p>
                <ul class="list-disc pl-5 space-y-1">
                  <li>{{ t('pages.campaign.stats.conversionTooltipCountingRuleItem1') }}</li>
                  <li>{{ t('pages.campaign.stats.conversionTooltipCountingRuleItem2') }}</li>
                </ul>
                <p class="font-semibold">{{ t('pages.campaign.stats.tooltipExampleTitle') }}</p>
                <ul class="list-disc pl-5 space-y-1">
                  <li>{{ t('pages.campaign.stats.conversionTooltipExampleItem1') }}</li>
                </ul>
              </div>
            </template>
          </AppTooltip>
        </div>
      </template>
      <template #content>
        <div class="flex flex-col flex-wrap items-baseline justify-start gap-3 md:flex-row">
          <span class="text-3xl font-extrabold text-primary">
            {{ statistics.visitsCount ? formatNumber(statistics.visitsCount) : '-' }}
          </span>
          <span v-if="statistics.visitsPercentage > 0" class="text-muted-foreground">
            {{ formatPercentage(statistics.visitsPercentage, statistics.visitsPercentage < 1 ? 2 : 1) }}
          </span>
        </div>
      </template>
    </AppStatsCard>
    <AppStatsCard>
      <template #title>
        <div class="flex items-center gap-2">
          <span class="truncate text-sm font-medium uppercase tracking-wider text-foreground-secondary">
            {{ t('pages.campaign.stats.generatedRevenue') }}
          </span>
          <AppTooltip width="w-[360px]" tooltip-position="bottom">
            <BaseIcon name="IconoirInfoCircle" class="h-4 w-4 cursor-pointer text-foreground-secondary" />
            <template #tooltip-content>
              <div class="flex flex-col gap-2 text-left">
                <p class="font-semibold">{{ t('pages.campaign.stats.revenueTooltipTitle') }}</p>
                <p class="font-semibold">{{ t('pages.campaign.stats.tooltipCountingRuleTitle') }}</p>
                <ul class="list-disc pl-5 space-y-1">
                  <li>{{ t('pages.campaign.stats.revenueTooltipCountingRuleItem1') }}</li>
                  <li>{{ t('pages.campaign.stats.revenueTooltipCountingRuleItem2') }}</li>
                  <li>{{ t('pages.campaign.stats.revenueTooltipCountingRuleItem3') }}</li>
                </ul>
                <p class="font-semibold">{{ t('pages.campaign.stats.tooltipExampleTitle') }}</p>
                <ul class="list-disc pl-5 space-y-1">
                  <li>{{ t('pages.campaign.stats.revenueTooltipExampleItem1') }}</li>
                </ul>
              </div>
            </template>
          </AppTooltip>
        </div>
      </template>
      <template #content>
        <div class="flex flex-col flex-wrap items-baseline justify-between gap-1 md:flex-row">
          <span class="text-3xl font-extrabold text-primary">
            {{ formatCurrency(statistics.generatedRevenue) }}
          </span>
        </div>
      </template>
    </AppStatsCard>
  </div>
</template>
